import type { LanguageKeyMap } from '$/store/languageStore/smartcatTranslation';
import imageNineGridPresetsConfig from './imageNineGridPresets.config.json';

/**
 * Team Flow — 后链路任务配置（视频 / 图片等媒体节点共用）
 *
 * ## 什么是后链路任务？
 * 用户在画布媒体节点（视频、图片等）上点击工具栏按钮后，会在源节点右侧分支 lane 新建一个
 * `data.isBacklink = true` 的子节点，连线为「源节点 output → 后链路节点 input」，
 * 由 MCP 工具执行处理（如高清增强、去字幕）。入参默认从上游节点的 `media_list` 自动映射。
 *
 * ## 关联代码（改配置时建议一并检查）
 * | 文件 | 作用 |
 * |------|------|
 * | `utils/excuteModelTask.ts` → `getBacklinkTaskParameters` | 按 `api_name` 查本文件，从上游 `media_list` 填充 `parameters` |
 * | `utils/executePostTask.ts` → `executePostTask` | 按源节点类型创建同类型后链路节点并触发执行 |
 * | `FlowContent/components/Menus/*TopMenu` | 工具栏按钮里 `execute*PostTask(ctx, node, XXX_CONFIG)` |
 *
 * ## 新增一条后链路（给 AI / 同学的检查清单）
 * 1. 确认后端 MCP 的 `api_name` 与接口要求的参数字段名（如 `video_url`、`image_url`）。
 * 2. 在本文件新增 `export const XXX_CONFIG: PostTaskConfig`，填写 `api_name`、`parameters`、`input_parameters`。
 * 3. 将常量注册到 `POST_TASK_CONFIG_BY_API_NAME`（**必须**，否则 `getBacklinkTaskParameters` 无法自动填参）。
 * 4. 填写 `nodeName`（后链路节点在画布上的展示名）。
 * 5. 在对应节点 TopMenu（或菜单）中引入该常量并调用 `executePostTask(ctx, node, XXX_CONFIG)`。
 *
 * ## `input_parameters` 映射规则（`getBacklinkTaskParameters`）
 * - key：写入任务 `parameters` 的字段名，需与 MCP 接口一致。
 * - `type === 'text'`：从上游 `media_list[].value` 拼接为字符串。
 * - `type` 为其他值（如 `video`、`image`）：从上游 `media_list[].url` 收集；字段为 *_list 时传数组，*_url 时传单个字符串。
 *
 * ## 配置示例（复制后改字段即可）
 * ```ts
 * export const IMAGE_UPSCALE_CONFIG: PostTaskConfig = {
 *   api_name: 'image_upscale',
 *   nodeName: '图片放大',
 *   parameters: { scale: 2 },
 *   input_parameters: {
 *     image_url: { type: 'image' },
 *   },
 * };
 * // POST_TASK_CONFIG_BY_API_NAME[IMAGE_UPSCALE_CONFIG.api_name] = IMAGE_UPSCALE_CONFIG;
 * ```
 */

type PostTaskFuncRunTab =
  | 'image_quality_enhance'
  | 'video_analyisis'
  | 'audio_seperage'
  | 'Erase'
  | 'Partial_Redraw';
type PostTaskWorkflowSendToolsName = 'skin_texture_optimize' | 'wipe' | 'nine_grid' | 'repaint';
type PostTaskNodeRunToolsName =
  | 'skin_texture_enhance'
  | 'repaint'
  | 'wipe'
  | 'image_quality_enhance'
  | 'video_filmvsr_enhance'
  | 'video_analysis'
  | 'audio_seperate';

interface PostTaskTracking {
  funcRunTab?: PostTaskFuncRunTab;
  workflowSendToolsName?: PostTaskWorkflowSendToolsName;
  nodeRunToolsName?: PostTaskNodeRunToolsName;
}

/**
 * 单条后链路 MCP 任务定义
 */
export interface PostTaskConfig {
  /** 后端 MCP 工具名，后链路节点的 `mcpInfo.api_name` 必须与此一致 */
  api_name: string;
  /** 后链路节点在画布上的展示名称（`data.name`） */
  nodeName: string;
  /** 后链路节点展示名对应的 Smartcat 文案 key，缺失时回退到 `nodeName` */
  nodeNameKey?: keyof LanguageKeyMap;
  /** 任务固定参数，会与从上游自动映射的字段合并后提交 */
  parameters: Record<string, unknown>;
  /** 主工具执行前依次调用的前置工具，随结果节点持久化并透传给 nodeexecute。 */
  pre_tools?: Array<{
    name: string;
    parameters: Record<string, unknown>;
  }>;
  /** 前置工具执行协议；当前提示词优化链路固定使用 prompt_optimize。 */
  pre_type?: 'prompt_optimize';
  /**
   * 需从上游节点 `media_list` 自动填充的参数字段
   * @see 文件顶部「input_parameters 映射规则」
   */
  input_parameters: Record<string, { type: string; }>;
  /** 大禹后链路埋点使用稳定枚举，不能复用会随语言变化的节点展示名。 */
  tracking?: PostTaskTracking;
}

/** 视频高清增强 */
export const VIDEO_HD_ENHANCE_CONFIG: PostTaskConfig = {
  api_name: 'video_enhance',
  nodeName: '视频变高清',
  nodeNameKey: 'ai_enhance_video',
  parameters: {
    hd_type: '2k',
  },
  input_parameters: {
    video_url: {
      type: 'video',
    },
  },
};

/** 视频影视级人像画质提升，固定使用算法的影视增强模式。 */
export const VIDEO_FILM_PORTRAIT_ENHANCE_CONFIG: PostTaskConfig = {
  api_name: 'video_filmvsr_enhance',
  nodeName: '影视级人像画质提升',
  nodeNameKey: 'robo_personal_film_hd',
  parameters: {
    sr_mode: 3,
  },
  input_parameters: {
    video_url: {
      type: 'video',
    },
  },
  tracking: {
    funcRunTab: 'image_quality_enhance',
    nodeRunToolsName: 'video_filmvsr_enhance',
  },
};

/** 视频智能去字幕 */
export const VIDEO_SUBTITLE_REMOVE_CONFIG: PostTaskConfig = {
  api_name: 'video_subtitle_remove',
  nodeName: '智能去字幕',
  nodeNameKey: 'intelligent_subtitle_removal',
  parameters: {},
  input_parameters: {
    video_url: {
      type: 'video',
    },
  },
};

/** 视频模板解析：单阶段 script_generate 返回 shots JSON，并落到脚本表格节点。 */
export const VIDEO_TEMPLATE_PARSE_CONFIG: PostTaskConfig = {
  api_name: 'script_generate',
  nodeName: 'Video story table',
  nodeNameKey: 'robo_video_story_table',
  parameters: {
    tool_knowledge_id: 200,
    summary: '视频解析',
  },
  input_parameters: {
    video_list: {
      type: 'video',
    },
  },
  tracking: {
    funcRunTab: 'video_analyisis',
    nodeRunToolsName: 'video_analysis',
  },
};

/** 为单阶段视频解析补充当前语言和本地化输入文案。 */
export function getLocalizedVideoTemplateParseConfig(
  language: string,
  summary: string,
): PostTaskConfig {
  return {
    ...VIDEO_TEMPLATE_PARSE_CONFIG,
    parameters: {
      ...VIDEO_TEMPLATE_PARSE_CONFIG.parameters,
      summary,
      language,
    },
  };
}

/** 完整音频分离工具：单次返回无声视频与原始音轨。 */
export const VIDEO_AUDIO_SEPARATE_CONFIG: PostTaskConfig = {
  api_name: 'video_aiaudio_separate',
  nodeName: 'Silent video',
  parameters: {
    sound: 'false',
  },
  input_parameters: {
    video_url: {
      type: 'video',
    },
  },
  tracking: {
    funcRunTab: 'audio_seperage',
    nodeRunToolsName: 'audio_seperate',
  },
};

/** 人声/背景声分离工具，固定返回无声视频、原始音频、人声音频和背景声音频。 */
export const AUDIO_SPLITER_CONFIG: PostTaskConfig = {
  api_name: 'audio_spliter_edit',
  nodeName: 'Audio separation',
  nodeNameKey: 'robo_audio_split',
  parameters: {},
  input_parameters: {
    video_url: {
      type: 'video',
    },
  },
  tracking: {
    funcRunTab: 'audio_seperage',
    nodeRunToolsName: 'audio_seperate',
  },
};

/** 视频分镜组合成 */
export const VIDEO_STITCH_CONFIG: PostTaskConfig = {
  api_name: 'video_stitch',
  nodeName: '视频合成',
  nodeNameKey: 'video_compositing',
  parameters: {
    prompt: '视频拼接，并保留视频原生',
  },
  input_parameters: {
    video_list: {
      type: 'video',
    },
  },
};

/** 图片打光 */
export const IMAGE_LIGHTING_CONFIG: PostTaskConfig = {
  api_name: 'image_weave_anything2img',
  nodeName: '打光',
  nodeNameKey: 'lighting',
  parameters: {
    effect_id: '491890',
  },
  input_parameters: {
    image_list: {
      type: 'image',
    },
  },
};

/** 图片多角度 */
export const IMAGE_MULTI_ANGLE_CONFIG: PostTaskConfig = {
  api_name: 'image_weave_anything2img',
  nodeName: '多角度',
  nodeNameKey: 'multi_angle',
  parameters: {
    effect_id: '491929',
  },
  input_parameters: {
    image_list: {
      type: 'image',
    },
  },
};

/** 图片生成 720 全景图 */
export const IMAGE_PANORAMA_720_CONFIG: PostTaskConfig = {
  api_name: 'image_weave_anything2img',
  nodeName: '720°全景图',
  nodeNameKey: '720_degree_panoramic_view',
  parameters: {
    effect_id: '492413',
  },
  input_parameters: {
    image_list: {
      type: 'image',
    },
  },
};

export type ImageNineGridPresetPostTaskConfig = PostTaskConfig & {
  /** 输入框与九宫格菜单共用的稳定预设标识。 */
  presetId: string;
  /** script_generate 提交为 tool_knowledge_id 的固定知识库 ID。 */
  templateId: number;
  /** 选中模板后优先使用的主生图模型及参数。 */
  recommendedModel: {
    modelId: string;
    parameters: Record<string, string | number | boolean>;
  };
};

type ImageNineGridRecommendedParameters = Record<string, string | number | boolean>;

/** 九宫格一期预设从 JSON 读取，统一走 script_generate 并以 tool_knowledge_id 区分能力。 */
export const IMAGE_NINE_GRID_PRESET_CONFIGS: ImageNineGridPresetPostTaskConfig[] = (
  imageNineGridPresetsConfig.templates
).map((template) => ({
  presetId: template.id,
  templateId: template.tool_knowledge_id,
  api_name: imageNineGridPresetsConfig.api_name,
  nodeName: template.label,
  nodeNameKey: template.labelKey as keyof LanguageKeyMap,
  parameters: { tool_knowledge_id: template.tool_knowledge_id },
  input_parameters: { image_list: { type: 'image' } },
  recommendedModel: {
    modelId: template.recommended_model.model_id,
    parameters: template.recommended_model.parameters as ImageNineGridRecommendedParameters,
  },
  tracking: template.id === 'skin_texture_optimize'
    ? {
      workflowSendToolsName: 'skin_texture_optimize',
      nodeRunToolsName: 'skin_texture_enhance',
    }
    : {
      workflowSendToolsName: 'nine_grid',
    },
}));

/** 图片高清放大 */
export const IMAGE_SUPERRES_CONFIG: PostTaskConfig = {
  api_name: 'image_enhance',
  nodeName: '图片变高清',
  nodeNameKey: 'ai_enhance_image',
  parameters: {
    hd_type: 'hd',
  },
  input_parameters: {
    image_url: {
      type: 'image',
    },
  },
  tracking: {
    funcRunTab: 'image_quality_enhance',
    nodeRunToolsName: 'image_quality_enhance',
  },
};

/** 图片抠图 */
export const IMAGE_CUTOUT_CONFIG: PostTaskConfig = {
  api_name: 'api_v1_sod_async',
  nodeName: '抠图',
  nodeNameKey: 'RoboNeo_Web_studio_cutout',
  parameters: {},
  input_parameters: {
    image_url: {
      type: 'image',
    },
  },
};

/** 图片局部重绘：image_list 顺序固定为原图、mask 图。 */
export const IMAGE_LOCAL_REDRAW_CONFIG: PostTaskConfig = {
  api_name: 'image_handsmear_edit',
  nodeName: 'Partial Redraw',
  nodeNameKey: 'robo_local_redraw',
  parameters: {},
  input_parameters: {
    image_list: {
      type: 'image',
    },
  },
  tracking: {
    funcRunTab: 'Partial_Redraw',
    workflowSendToolsName: 'repaint',
    nodeRunToolsName: 'repaint',
  },
};

/** 图片擦除：mask_url 为可选参数，编辑器有选区时显式传入。 */
export const IMAGE_REMOVE_CONFIG: PostTaskConfig = {
  api_name: 'api_text_remover_img_sd_async',
  nodeName: 'Erase',
  nodeNameKey: 'robo_erase',
  parameters: {},
  input_parameters: {
    image_url: {
      type: 'image',
    },
  },
  tracking: {
    funcRunTab: 'Erase',
    workflowSendToolsName: 'wipe',
    nodeRunToolsName: 'wipe',
  },
};

export const POST_TASK_CONFIGS = [
  VIDEO_HD_ENHANCE_CONFIG,
  VIDEO_FILM_PORTRAIT_ENHANCE_CONFIG,
  VIDEO_SUBTITLE_REMOVE_CONFIG,
  VIDEO_TEMPLATE_PARSE_CONFIG,
  VIDEO_AUDIO_SEPARATE_CONFIG,
  AUDIO_SPLITER_CONFIG,
  VIDEO_STITCH_CONFIG,
  IMAGE_LIGHTING_CONFIG,
  IMAGE_MULTI_ANGLE_CONFIG,
  IMAGE_PANORAMA_720_CONFIG,
  ...IMAGE_NINE_GRID_PRESET_CONFIGS,
  IMAGE_SUPERRES_CONFIG,
  IMAGE_CUTOUT_CONFIG,
  IMAGE_LOCAL_REDRAW_CONFIG,
  IMAGE_REMOVE_CONFIG,
] as const;

/** 构建 api_name 索引时保留首个配置，兼容旧的按 api_name 查询路径。 */
function addFirstApiNameConfig(acc: Record<string, PostTaskConfig>, config: PostTaskConfig) {
  if (!acc[config.api_name]) {
    acc[config.api_name] = config;
  }
  return acc;
}

/**
 * 全局索引：api_name → PostTaskConfig
 * 新增配置后务必在此注册，否则后链路节点执行任务时无法自动从上游取参
 */
export const POST_TASK_CONFIG_BY_API_NAME: Record<string, PostTaskConfig> = POST_TASK_CONFIGS.reduce(
  addFirstApiNameConfig,
  {} as Record<string, PostTaskConfig>,
);

/** 从后链路固定参数中读取 effect_id，用于区分相同 api_name 的工具。 */
function getPostTaskEffectId(parameters?: Record<string, unknown>): string {
  const effectId = parameters?.effect_id;
  return effectId == null ? '' : String(effectId);
}

/** 拼接 api_name + effect_id 的唯一索引 key。 */
function getPostTaskApiEffectKey(apiName: string, effectId: string): string {
  return `${apiName}::${effectId}`;
}

/** 构建 api_name + effect_id 索引，保证同接口多效果能稳定区分。 */
function addApiEffectConfig(acc: Record<string, PostTaskConfig>, config: PostTaskConfig) {
  const effectId = getPostTaskEffectId(config.parameters);
  if (effectId) {
    acc[getPostTaskApiEffectKey(config.api_name, effectId)] = config;
  }
  return acc;
}

/** 全局索引：api_name + effect_id → PostTaskConfig。 */
const POST_TASK_CONFIG_BY_API_EFFECT_ID: Record<string, PostTaskConfig> = POST_TASK_CONFIGS.reduce(
  addApiEffectConfig,
  {} as Record<string, PostTaskConfig>,
);

/**
 * 把历史画布参数迁移为工具当前协议；未声明迁移规则的工具保持参数不变。
 */
export function normalizePostTaskParameters(
  apiName: string | undefined,
  parameters: Record<string, any>,
): Record<string, any> {
  const nextParameters = { ...parameters };
  if (apiName === 'audio_spliter_edit') {
    const videoUrl = nextParameters.video_url ?? nextParameters.audio_url;
    if (videoUrl !== undefined && videoUrl !== null) {
      nextParameters.video_url = videoUrl;
    }
    delete nextParameters.audio_url;
    delete nextParameters.task_type;
    return nextParameters;
  }

  if (apiName !== 'script_generate') return nextParameters;

  const knowledgeId = nextParameters.tool_knowledge_id ?? nextParameters.template_id;
  if (knowledgeId !== undefined && knowledgeId !== null) {
    nextParameters.tool_knowledge_id = knowledgeId;
  }
  delete nextParameters.template_id;
  return nextParameters;
}

/** 读取 script_generate 知识库 ID，并兼容历史画布中的 template_id。 */
function getPostTaskKnowledgeId(parameters?: Record<string, unknown>): string {
  const knowledgeId = parameters?.tool_knowledge_id ?? parameters?.template_id;
  return knowledgeId == null ? '' : String(knowledgeId);
}

/** 拼接 api_name + tool_knowledge_id 的唯一索引 key。 */
function getPostTaskApiKnowledgeKey(apiName: string, knowledgeId: string): string {
  return `${apiName}::${knowledgeId}`;
}

/** 构建 api_name + tool_knowledge_id 索引，保证 script_generate 能力能稳定区分。 */
function addApiKnowledgeConfig(acc: Record<string, PostTaskConfig>, config: PostTaskConfig) {
  const knowledgeId = getPostTaskKnowledgeId(config.parameters);
  if (knowledgeId) {
    acc[getPostTaskApiKnowledgeKey(config.api_name, knowledgeId)] = config;
  }
  return acc;
}

/** 全局索引：api_name + tool_knowledge_id → PostTaskConfig。 */
const POST_TASK_CONFIG_BY_API_KNOWLEDGE_ID: Record<string, PostTaskConfig> = POST_TASK_CONFIGS.reduce(
  addApiKnowledgeConfig,
  {} as Record<string, PostTaskConfig>,
);

/**
 * 按 MCP 信息解析后链路配置；同 api_name 下优先使用知识库 ID 或 effect_id 精确匹配。
 * script_generate 新协议读取 tool_knowledge_id，历史 template_id 仅用于恢复旧画布。
 */
export function resolvePostTaskConfigByMcpInfo(
  apiName: string | undefined,
  parameters?: Record<string, unknown>,
): PostTaskConfig | undefined {
  if (!apiName) return undefined;
  const knowledgeId = getPostTaskKnowledgeId(parameters);
  if (knowledgeId) {
    const knowledgeConfig = POST_TASK_CONFIG_BY_API_KNOWLEDGE_ID[
      getPostTaskApiKnowledgeKey(apiName, knowledgeId)
    ];
    if (knowledgeConfig) return knowledgeConfig;
    // script_generate 的知识库决定具体能力，未知 ID 不能回退成默认的视频解析。
    if (apiName === 'script_generate') return undefined;
  }
  const effectId = getPostTaskEffectId(parameters);
  if (effectId) {
    const effectConfig = POST_TASK_CONFIG_BY_API_EFFECT_ID[getPostTaskApiEffectKey(apiName, effectId)];
    if (effectConfig) return effectConfig;
  }
  return POST_TASK_CONFIG_BY_API_NAME[apiName];
}
